import { useState } from 'react'
import { AlarmClock, ChevronDown, Pause, Pencil, Play, Plus, Sparkles, Trash2 } from 'lucide-react'
import type { Task } from '@shared/types'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { TaskIcon } from '../tasks/TasksPage'
import { runText, untilText, useNow } from '../tasks/schedule-ui'

/**
 * What wakes the AI in this chat: a slim line above the composer ("2 scheduled · next 09:25:00 ET in
 * 3h 12m") that opens into the list — run now, pause, edit, delete, add one.
 */
export function ChatSchedules() {
  const chatId = useStore((s) => s.activeChatId)
  const tasks = useStore((s) => s.chatTasks)
  const openEditor = useStore((s) => s.openTaskEditor)
  const [open, setOpen] = useState(false)
  const now = useNow()
  const live = tasks.filter((t) => !t.doneAt)
  if (!chatId || !live.length) return null
  const active = live.filter((t) => t.enabled && t.nextRunAt)
  const next = active.reduce<Task | null>((a, t) => (!a || t.nextRunAt! < a.nextRunAt! ? t : a), null)

  return (
    <div className="mx-auto w-full max-w-3xl px-4">
      <div className="rounded-xl border border-[var(--line-soft)] bg-[var(--color-surface-1,var(--color-base))]">
        <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11.5px] text-fg-subtle hover:text-fg">
          <AlarmClock size={13} className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            {live.length} scheduled here
            {next ? (
              <span className="num">
                {' '}
                · next “{next.title}” {runText(next.nextRunAt!, next.schedule.tz).split(' ').slice(3).join(' ')} · {untilText(next.nextRunAt!, now)}
              </span>
            ) : (
              ' · all paused'
            )}
          </span>
          <ChevronDown size={13} className={cn('shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="border-t border-[var(--line-soft)] px-2 py-1.5">
            {live.map((t) => (
              <ScheduleRow key={t.id} t={t} now={now} />
            ))}
            <button
              onClick={() => openEditor({ preset: { runIn: 'chat', chatId } })}
              className="mt-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11.5px] text-fg-subtle hover:text-fg"
            >
              <Plus size={12} /> Schedule another
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ScheduleRow({ t, now }: { t: Task; now: number }) {
  const toggle = useStore((s) => s.toggleTask)
  const remove = useStore((s) => s.removeTask)
  const runNow = useStore((s) => s.runTaskNow)
  const openEditor = useStore((s) => s.openTaskEditor)
  const btn = 'inline-flex h-6 w-6 items-center justify-center rounded-md text-fg-faint hover:bg-[var(--color-surface-2)] hover:text-fg'
  return (
    <div className="group flex items-center gap-2 rounded-lg px-1.5 py-1">
      <span className={cn('shrink-0', t.enabled ? 'text-fg-muted' : 'text-fg-faint')}>
        <TaskIcon t={t} size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[12px] text-fg">
          <span className="truncate">{t.title}</span>
          {t.createdBy === 'ai' && <Sparkles size={10} className="shrink-0 text-fg-faint" aria-label="scheduled by the AI" />}
        </div>
        <div className="num truncate text-[11px] text-fg-subtle">
          {t.scheduleText}
          {t.enabled && t.nextRunAt ? ` · ${untilText(t.nextRunAt, now)}` : ' · paused'}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 opacity-70 group-hover:opacity-100">
        <button title="Run now" onClick={() => void runNow(t.id)} className={btn}>
          <Play size={12} />
        </button>
        <button title={t.enabled ? 'Pause' : 'Resume'} onClick={() => void toggle(t.id, !t.enabled)} className={btn}>
          {t.enabled ? <Pause size={12} /> : <Play size={12} className="text-up" />}
        </button>
        <button title="Edit" onClick={() => openEditor({ task: t })} className={btn}>
          <Pencil size={12} />
        </button>
        <button
          title="Delete"
          onClick={() => {
            if (confirm(`Delete “${t.title}”?`)) void remove(t.id)
          }}
          className={btn}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  )
}
